import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {IOpportunity, OpportunityStatus} from '../../model/opportunity.model';
import {ContractStatus, ISupervisor, SupervisorLabel} from '../../model/supervisor.model';
import {ContactInfo, StudentDisplayData, TableColumn, TableData, TableDataType} from '../../model/tableData.model';
import {IOrganization, OrganizationStatus} from "../../model/organization.model";
import {TableActionTypes} from "../../constants/table-action-types.constants";
import {EventLogActionType} from '../../model/event-log.model';
import {stateHelperFunctions} from "../../../core/store/stateHelperFunctions";
import {ITableOptions} from "../../model/table-options.model";
import {ServiceStatus} from "../../model/service-registration.model";
import {updateNavigationStateAction} from "../../../core/store/navigation/navigation.actions";
import {Store} from "@ngrx/store";
import {catchError, map, Observable, of} from "rxjs";
import {
  selectActionItemsByOpportunityId,
} from "../../../core/store/service-registrations/service-registrations.selectors";
import {SharedPrimitivesModule} from "../../shared-primitives.module";
import {RatingWidgetComponent} from "../rating-widget/rating-widget.component";
import {BlacklistModalComponent} from "../modal/modal-views/blacklist-modal/blacklist-modal.component";
import {DecimalToIntegerPipe} from "../../pipes/decimal-to-integer.pipe";
import {EmailCleanupPipe} from "../../pipes/email-cleanup.pipe";
import {EnumConstantPipe} from "../../pipes/enumConstant.pipe";
import {TermPipe} from "../../pipes/term.pipe";
import {IconComponent} from "../../icon/icon.component";
import {ActionDatePipe} from "../../pipes/action-date.pipe";
import {PhoneNumberPipe} from "../../pipes/phone-number.pipe";

@Component({
  selector: 'app-dynamic-table-widget',
  templateUrl: './dynamic-table-widget.component.html',
  styleUrls: ['./dynamic-table-widget.component.scss'],
  standalone: true,
  imports: [
    SharedPrimitivesModule,
    IconComponent,
    RatingWidgetComponent,
    BlacklistModalComponent,
    ActionDatePipe,
    DecimalToIntegerPipe,
    EmailCleanupPipe,
    EnumConstantPipe,
    PhoneNumberPipe,
    TermPipe
  ]
})
export class DynamicTableWidgetComponent implements OnInit, OnChanges {
  @Input() columns: TableColumn[] = [];
  @Input() data: TableData[] = [];
  @Input() backendPaginationToggle: boolean = false;

  @Input() actionTypes: TableActionTypes[] = [];
  @Input() hasActionColumn: boolean = false;
  @Input() useAutoTableLayout: boolean = false;
  @Input() hasCheckboxColumn: boolean = false;
  @Input() hasViewRequestColumn: boolean = false;
  @Input() hasReplaceLeadSupervisorColumn: boolean = false;

  @Input() hasReviewRequestColumn: boolean = false;
  @Input() hasViewDetailsColumn: boolean = false;
  @Output()
  public reviewRequestEvent: EventEmitter<string> = new EventEmitter<string>();

  @Input() isAnnouncement: boolean = false;
  @Input() canExportData: boolean = false;
  @Input() isSubmittedEvaluations: boolean = false;

  @Input() isActiveVolunteers: boolean = false;
  @Input() isFailedSubmission: boolean = false;
  @Input() hasViewSubmissionColumn: boolean = false;

  @Input() recordChanged: TableData | undefined;

  @Output()
  public submitEvalEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public viewEvalsEvent: EventEmitter<string> = new EventEmitter<string>();

  @Input() hasViewColumn: boolean = false;
  @Input() hasEditColumn: boolean = false;
  @Output()
  public viewOpportunityEvent: EventEmitter<string> = new EventEmitter<string>();

  @Input() isPaginated: boolean = false;
  @Input() pageSize: number = 25;
  public pageSizeOptions: number[] = [5, 10, 15, 20, 25];
  public currentPage: number = 1;
  @Input() tableOptions: ITableOptions | undefined = undefined;

  @Output() userRecordSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectedRecordIds: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() areRecordsSelected: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() deactivateAnnouncementEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() activateAnnouncementEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() viewAnnouncementEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() deleteAnnouncementEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output() viewOpportunitiesEvent: EventEmitter<{ opportunities: IOpportunity[], name: string }> = new EventEmitter<{
    opportunities: IOpportunity[],
    name: string
  }>();

  @Output() viewOrganizationsEvent: EventEmitter<{ organizations: IOrganization[], name: string }> = new EventEmitter<{
    organizations: IOrganization[],
    name: string
  }>();

  @Output() viewSupervisorsEvent: EventEmitter<{ supervisors: ISupervisor[], name: string }> = new EventEmitter<{
    supervisors: ISupervisor[],
    name: string
  }>();

  @Output() impersonateEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() impersonateByIdEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() blacklistEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() inviteEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() changeStatusEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() viewRequestEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() viewEditTermEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output() makeOppLeadEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() removeOppSupervisorEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() replaceLeadSupervisorEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectLeadSupervisorEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() addBypassEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() clearBypassesEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() addCreditBypassEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() clearCreditBypassesEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() clearOptOutEvent: EventEmitter<any> = new EventEmitter<any>();

  @Input() hasSwapSupervisorButton: boolean = false;
  @Output() swapSupervisorEvent: EventEmitter<string> = new EventEmitter<string>();

  public modalRow: any = undefined;
  @Output() tableOptionsChangeEvent: EventEmitter<ITableOptions> = new EventEmitter<ITableOptions>();
  @Output() tableDataChangeEvent: EventEmitter<TableData[]> = new EventEmitter<TableData[]>();

  @Output() retrySubmissionEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() viewSubmissionErrorEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() manualResolutionEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() additionalRegistrationEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output() exportDataEvent: EventEmitter<string> = new EventEmitter<any>();

  public isBlacklistModalOpen: boolean = false;
  @Input() blacklistType: string | undefined;

  @Input() checkIfAllSelectedFromRegistrationRequests: boolean = false;

  @Input() public hasManualResolution: any;

  public isLead: boolean = false;

  @Input() public canViewEvals: boolean = false;

  public disabledOppRequestTooltip: string = 'You cannot review this opportunity until its organization has been approved.';

  @Input() public hasEditSupervisorColumn: boolean = false;

  @Output() editSupervisorEvent = new EventEmitter<string>();

  private readonly ACTIVE_STATUSES = this.activeStatuses;
  private readonly PENDING_STATUSES = this.pendingStatuses;
  private readonly INACTIVE_STATUSES = this.inactiveStatuses;
  private readonly DATA_ERROR_STATUSES = ['NONE'];
  private readonly NEW_ICON_ACTION_TYPES = this.newIconActionTypes;
  private readonly APPROVED_ICON_ACTION_TYPES = this.approvedIconActionTypes;
  private readonly REJECTED_ICON_ACTION_TYPES = this.rejectedIconActionTypes;
  private readonly PENDING_ICON_ACTION_TYPES = this.pendingIconActionTypes;

  constructor(private router: Router, private elementRef: ElementRef, private store: Store) {
  }

  ngOnInit(): void {
    // make a local copy of the data, so we can modify and sort it instead of the read-only copy from the state
    this.data = structuredClone(this.data)

    this.applyTableOptions(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (propName === 'recordChanged') {
        if (!!changes[propName].currentValue && !!changes[propName].currentValue?.id) {
          this.data = stateHelperFunctions.replaceItemInArray(this.data, changes[propName].currentValue);
          this.tableDataChangeEvent.emit(this.data.slice());
        }
      } else if (propName === 'data') {
        this.data = structuredClone(this.data);
      } else if (propName === 'tableOptions') {
        this.tableOptions = changes[propName].currentValue;
        this.applyTableOptions();
      }
    }

    this.checkSelectAllCheckboxIfApplicable();
  }

  private applyTableOptions(runSort: boolean = false) {
    if (!!this.tableOptions) {
      this.currentPage = this.tableOptions.currentPage;
      this.pageSize = this.tableOptions.pageSize;

      if (!!this.tableOptions.sortColumn && runSort && !this.backendPaginationToggle) {
        const column = this.columns.find(c => {
          if (c.header === this.tableOptions!.sortColumn!.header) {
            if (this.tableOptions!.sortColumn!.sortDirection === 'desc') {
              c.sortDirection = 'desc';
            }

            return true;
          } else {
            return false;
          }
        })!

        this.sort(column);
      }
    }
  }

  public changePageSize(): void {
    if (!!this.tableOptions) {
      this.tableOptions = {
        ...this.tableOptions,
        pageSize: this.pageSize,
        currentPage: 1
      }
      this.tableOptionsChangeEvent.emit(this.tableOptions);
    }
  }

  public getSortIcon(column: TableColumn): string {
    if (this.backendPaginationToggle && this.tableOptions?.sortColumn?.header === column.header) {
      return this.getSortDirectionIcon(this.tableOptions?.sortColumn?.sortDirection);
    }
    return this.getSortDirectionIcon(column.sortDirection);
  }

  private getSortDirectionIcon(direction?: string): string {
    return direction === "asc" ? "sort-asc" : direction === "desc" ? "sort-desc" : "sort";
  }

  public sort(column: TableColumn) {
    if (this.hasSort(column)) {
      column.sortDirection = column.sortDirection === 'asc' ? 'desc' : 'asc';

      if (!this.backendPaginationToggle) {
        this.data.sort((a, b) => {
          let valueA = a[column.field];
          let valueB = b[column.field];

          const stringValueA = String(valueA).toLowerCase();
          const stringValueB = String(valueB).toLowerCase();

          if (typeof valueA === 'number' && typeof valueB === 'number') {
            return column.sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
          } else {
            const comparisonResult = stringValueA.localeCompare(stringValueB);
            return column.sortDirection === 'asc' ? comparisonResult : -comparisonResult;
          }
        });
      }

      if (!!this.tableOptions) {
        this.tableOptions = {
          ...this.tableOptions,
          sortColumn: this.copyColumn(column)
        };
        this.tableOptionsChangeEvent.emit(this.tableOptions);
      }
    }
  }

  // An annoying byproduct of state, to get a writable copy of an object instead of the read-only version in the state
  private copyColumn(column: TableColumn): TableColumn {
    return <TableColumn>{
      field: column.field,
      dataType: column.dataType,
      header: column.header,
      sortDirection: column.sortDirection
    }
  }

  public toggleCheckbox(index: number): void {
    this.data[index].isSelected = !this.data[index].isSelected;
    this.updateSelectedRecords();
  }

  public toggleAllCheckboxes(event: any): void {
    const isChecked = event.target.checked;
    this.data.forEach(row => (row.isSelected = isChecked));
    this.updateSelectedRecords();
  }

  private updateSelectedRecords(): void {
    let selectedRecords = this.data.filter(row => row.isSelected).map(row => row.id as string);
    this.selectedRecordIds.emit(selectedRecords);
    this.areRecordsSelected.emit(this.selectedRecordIds.length !== 0);
  }

  public isStatus(columnData: TableDataType, header: string) {
    if (this.isAction(header)) return 'false';

    const formattedColumnData = String(columnData).toUpperCase();

    switch (true) {
      case this.ACTIVE_STATUSES.includes(formattedColumnData):
        return 'status-green';
      case this.PENDING_STATUSES.includes(formattedColumnData):
        return 'status-yellow';
      case this.INACTIVE_STATUSES.includes(formattedColumnData):
        return 'status-gray';
      case this.DATA_ERROR_STATUSES.includes(formattedColumnData):
        return 'status-red';
      default:
        return 'false';
    }
  }

  public isSupervisorLabel(field: string) {
    return field === 'supervisorLabel';
  }

  public supervisorLabel(columnData: TableDataType) {
    if (columnData === SupervisorLabel.ORG_LEAD) {
      return 'label-org-lead';
    }
    if (columnData === SupervisorLabel.OPP_LEAD) {
      return 'label-opp-lead';
    }
    return SupervisorLabel.NONE;
  }

  public isTimeLogged(header: string) {
    return header === 'timeLogged';
  }

  public isTerm(header: string) {
    return header === 'term';
  }

  public isOrgName(header: string) {
    return header === 'orgName';
  }

  public isEmail(header: string) {
    return header === 'email';
  }

  public isAction(header: string) {
    return header === 'action';
  }

  public isOrgType(header: string) {
    return header === 'orgType';
  }

  public isDate(header: string) {
    return header === 'date';
  }

  public isDateTime(dataType?: string): boolean {
    if (!dataType) return false;
    return dataType === 'dateTime';
  }

  public isStudentId(header: string) {
    return header === 'studentId';
  }

  public isRating(header: string) {
    return header === 'rating';
  }

  public ratingAsString(columnData: TableDataType) {
    if (columnData === undefined || columnData === null) {
      return null;
    } else {
      return String(columnData);
    }

  }

  public getEventActionIcon(columnData: TableDataType) {
    const formattedColumnData = String(columnData).toUpperCase();

    switch (true) {
      case this.NEW_ICON_ACTION_TYPES.includes(formattedColumnData):
        return 'feather';
      case this.PENDING_ICON_ACTION_TYPES.includes(formattedColumnData):
        return 'clock';
      case this.APPROVED_ICON_ACTION_TYPES.includes(formattedColumnData):
        return 'file-check';
      case this.REJECTED_ICON_ACTION_TYPES.includes(formattedColumnData):
        return 'x-square';
      default:
        return '';
    }
  }

  public getEventChanges(row: any): string {
    if (!row['additionalData']) {
      return '';
    } else {
      return row['additionalData'];
    }
  }

  public getActionSelected(row: any, event: any, actionSelect: HTMLSelectElement) {
    let value = event.target.value;
    if (value == TableActionTypes.IMPERSONATE || value == TableActionTypes.AUDIT) {
      this.columns.forEach((column) => {
        if (column.header == 'email') {
          if (row[column.field]) {
            this.impersonateEvent.emit(row[column.field]);
          }
        } else if (column.header == 'contact') {
          if (row[column.field]) {
            this.impersonateEvent.emit(this.getContactInfo(row[column.field]).email);
          }
        }
      });
    } else if (value == TableActionTypes.IMPERSONATE_SUPERVISOR) {
      if (row.supervisorId) {
        this.impersonateByIdEvent.emit(row.supervisorId);
      } else if (!!row.lead) {
        this.impersonateByIdEvent.emit(row.lead.id);
      }
    } else if (value == TableActionTypes.BLACKLIST) {
      if (row.id) {
        this.modalRow = row;
        if (row.organizations) {
          let supOrgs: IOrganization[] = this.modalRow.organizations;
          supOrgs.forEach(org => {
            if (org.supervisorId === row.id) {
              this.isLead = true;
            }
          })
        }
        this.isBlacklistModalOpen = true;
      }
    } else if (value == TableActionTypes.RESEND_INVITE) {
      if (row.id) {
        this.inviteEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.DEACTIVATE || value == TableActionTypes.ACTIVATE || value == TableActionTypes.ARCHIVE) {
      if (row.id) {
        this.changeStatusEvent.emit({id: row.id, status: value});
      }
    } else if (value == TableActionTypes.MAKE_OPPORTUNITY_LEAD) {
      if (row.id) {
        this.makeOppLeadEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.REMOVE_OPPORTUNITY_SUPERVISOR) {
      if (row.id) {
        this.removeOppSupervisorEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.REPLACE_LEAD_SUPERVISOR) {
      if (row.id) {
        this.replaceLeadSupervisorEvent.emit(row);
      }
    } else if (value == TableActionTypes.ADD_BYPASS) {
      if (row.id) {
        this.addBypassEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.CLEAR_BYPASS) {
      if (row.id) {
        this.clearBypassesEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.ADD_CREDIT_BYPASS) {
      if (row.id) {
        this.addCreditBypassEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.CLEAR_CREDIT_BYPASS) {
      if (row.id) {
        this.clearCreditBypassesEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.CLEAR_OPT_OUT) {
      if (row.id) {
        this.clearOptOutEvent.emit(row.id);
      }
    } else if (value == TableActionTypes.LOAD_VOLUNTEER) {
      if (row.email) {
        this.userRecordSelected.emit(row.email);
      }
    } else if (value == TableActionTypes.ADDITIONAL_REGISTRATION) {
      if (row.id) {
        this.additionalRegistrationEvent.emit(row.id);
      }
    }

    actionSelect.value = 'Actions';
  }

  public shouldNotShowActionInDropdown(action: TableActionTypes, row: TableData) {
    return (action === 'Deactivate' && row['status'] === 'INACTIVE') ||
      (action === 'Activate' && row['status'] === 'ACTIVE') ||
      (action === 'Activate' && row['status'] === 'NEW') ||
      (action === 'Blacklist' && row['status'] === 'BLACKLISTED') ||
      (action === 'Make Opportunity Lead' && (row['status'] !== 'ACTIVE' && row['status'] !== 'NEW')) ||
      (action === 'Make Opportunity Lead' && row['isOppLead'] === true) ||
      (action === TableActionTypes.ADDITIONAL_REGISTRATION && row['status'] !== 'PASSED') ||
      (action === 'Remove Opportunity Supervisor' && row['isOppLead'] === true) ||
      (action === 'Replace Lead Supervisor' && row['isOppLead'] !== undefined && row['isOppLead'] !== true)
  }

  public confirmBlacklist(event: any) {
    let organizations: IOrganization[] = this.modalRow.organizations ? this.modalRow.organizations : [];
    this.blacklistEvent.emit({
      id: this.modalRow.id,
      reason: event.reason,
      organizations: organizations,
      replacementSup: event.replacementSup
    });
    this.modalRow = undefined;
    this.isBlacklistModalOpen = false;
  }

  public closeModals() {
    this.isLead = false;
    this.isBlacklistModalOpen = false;
  }

  public hasSupervisorData(dataType: string | undefined) {
    return dataType === 'ISupervisor'
  }

  public hasStackableOppData(dataType: string | undefined) {
    return dataType === 'IOpportunity[]';
  }

  public hasStackableOrgData(dataType: string | undefined) {
    return dataType === 'IOrganization[]';
  }

  public hasStackableSupData(dataType: string | undefined) {
    return dataType === 'ISupervisor[]';
  }

  public hasStackableContactData(dataType: string | undefined) {
    return dataType === 'ContactInfo';
  }

  public hasStringArray(dataType: string | undefined) {
    return dataType === 'string[]';
  }

  public hasStudentDisplayData(dataType: string | undefined) {
    return dataType === 'StudentDisplayData';
  }

  public getStackableDataCount(columnData: TableDataType) {
    return Array.isArray(columnData) ? columnData.length : 1;
  }

  public hasNoSpecialFormatting(columnData: TableDataType, header: string, dataType: string | undefined) {
    return this.isStatus(columnData, header) === 'false' &&
      !this.isOrgName(header) &&
      !this.hasStackableOppData(dataType) &&
      !this.hasStackableOrgData(dataType) &&
      !this.hasStackableSupData(dataType) &&
      !this.hasStackableContactData(dataType) &&
      !this.hasSupervisorData(dataType) &&
      !this.hasStringArray(dataType) &&
      !this.isDateTime(dataType) &&
      !this.isEmail(header) &&
      !this.isDate(header) &&
      !this.hasStudentDisplayData(dataType) &&
      !this.isTimeLogged(header) &&
      !this.isTerm(header) &&
      !this.isAction(header) &&
      !this.isOrgType(header) &&
      !this.isStudentId(header) &&
      !this.isRating(header) &&
      !this.isSupervisorLabel(header)
  }

  public getOpportunityData(columnData: TableDataType) {
    return <IOpportunity[]>columnData;
  }

  public getOrganizationData(columnData: TableDataType) {
    return <IOrganization[]>columnData;
  }

  public getSupervisorData(columnData: TableDataType) {
    return <ISupervisor>columnData;
  }

  public getSupervisorListData(columnData: TableDataType) {
    return <ISupervisor[]>columnData;
  }

  public getStringArrayData(columnData: TableDataType) {
    return <string[]>columnData;
  }

  public getStudentDisplayData(columnData: TableDataType) {
    return <StudentDisplayData>columnData;
  }

  public getContactInfo(columnData: TableDataType) {
    return <ContactInfo>columnData;
  }

  get totalPages(): number {
    //If backend pagination, determine the total based on the tableOptions
    if (this.tableOptions?.totalRecords && this.backendPaginationToggle) {
      return Math.ceil(this.tableOptions.totalRecords / this.pageSize);
    } else {
      return Math.ceil(this.data.length / this.pageSize);
    }
  }

  get paginatedData(): TableData[] {
    if ((this.isPaginated && this.backendPaginationToggle) || !this.isPaginated) {
      return this.data;
    } else {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.data.slice(startIndex, endIndex);
    }
  }

  get pageNumbers(): number[] {
    const totalPages = this.totalPages;
    return Array.from({length: totalPages}, (_, index) => index + 1);
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.emitChangePageEvent();
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.emitChangePageEvent();
    }
  }

  goToPage(pageNumber: number) {
    this.currentPage = pageNumber;
    this.emitChangePageEvent();
  }

  private emitChangePageEvent() {
    if (!!this.tableOptions) {
      this.tableOptions = {
        ...this.tableOptions,
        currentPage: this.currentPage
      };
      this.tableOptionsChangeEvent.emit(this.tableOptions);
    }
  }

  public navigateToRecord(row: TableData) {
    let currentUrl = this.router.url;
    this.updateNavigationState(currentUrl);
    if (row.id && row.objectType) {
      if (currentUrl.includes('/admin/organizations/all') || currentUrl.includes('/admin/organizations/event-logs') && row.objectType.toLowerCase() == 'organization') {
        this.router.navigate(['/', 'admin', 'organization', 'details', row.id]);
      } else if (currentUrl.includes('/supervisor/past-opportunities')) {
        this.router.navigate(['/', 'supervisor', 'my-opportunities', row.id, 'active-volunteers'])
      }
    } else if (currentUrl.includes('/admin/supervisors/all') || currentUrl.includes('/admin/opportunities/all')) {
      let orgArray: IOrganization[] = row['organizations'] as IOrganization[];
      let org: IOrganization = orgArray[0];
      this.router.navigate(['/', 'admin', 'organization', 'details', org.id]);
    } else if (currentUrl.includes('/admin/placements/all')) {
      this.router.navigate(['/', 'admin', 'organization', 'details', row['orgId']]);
    }
  }

  public updateNavigationState(currentUrl: string) {
    this.store.dispatch(updateNavigationStateAction({data: currentUrl}));
  }

  public oppRequestHasPendingOrg(columnData: TableData) {
    return columnData.orgStatusBlocksOppApproval;
  }

  protected getViewRequestAriaLabel(name: TableDataType): string {
    return typeof name === 'string' && name.trim()
      ? `View Request for ${name}`
      : `View Request`;
  }

  public emitReviewRequestEvent(id: string | undefined) {
    if (id) {
      this.reviewRequestEvent.emit(id);
    }
  }

  public emitSubmitEvalEvent(id: string | undefined) {
    if (id) {
      this.submitEvalEvent.emit(id);
    }
  }

  public emitViewEvalsEvent(id: string | undefined) {
    if (id) {
      this.viewEvalsEvent.emit(id);
    }
  }

  public emitViewOpportunityEvent(id: string | undefined) {
    if (id) {
      this.viewOpportunityEvent.emit(id);
    }
  }

  public emitViewOpportunitiesEvent(opportunities: TableDataType, name: TableDataType) {
    if (Array.isArray(opportunities)) {
      this.viewOpportunitiesEvent.emit({opportunities: opportunities as IOpportunity[], name: <string>name});
    }
  }

  public emitViewOrganizationsEvent(organizations: TableDataType, name: TableDataType) {
    if (Array.isArray(organizations)) {
      this.viewOrganizationsEvent.emit({organizations: organizations as IOrganization[], name: <string>name});
    }
  }

  public emitViewSupervisorsEvent(supervisors: TableDataType, name: TableDataType) {
    if (Array.isArray(supervisors)) {
      this.viewSupervisorsEvent.emit({supervisors: supervisors as ISupervisor[], name: <string>name});
    }
  }

  public emitChangeAnnouncementStatusEvent(id: string | undefined, activate: boolean) {
    if (id) {
      activate ? this.activateAnnouncementEvent.emit(id) : this.deactivateAnnouncementEvent.emit(id);
    }
  }

  public emitDeleteAnnouncementEvent(id: string | undefined) {
    if (id) {
      this.deleteAnnouncementEvent.emit(id);
    }
  }

  public emitViewAnnouncementEvent(id: string | undefined) {
    if (id) {
      this.viewAnnouncementEvent.emit(id);
    }
  }

  public hasSort(column: TableColumn) {
    if (column.canBeSorted === false) {
      return false;
    }

    return this.isColumnDataTypeSortable(column.dataType) && column.header !== '';
  }

  private isColumnDataTypeSortable(dataType: string | undefined) {
    return dataType === 'number' || dataType === 'string' || dataType === 'boolean' || dataType === 'dateTime';
  }

  public emitViewRequestEvent(id: string | undefined) {
    if (id) {
      this.viewRequestEvent.emit(id);
    }
  }

  public emitViewEditTermEvent(id: string | undefined) {
    if (id) {
      this.viewEditTermEvent.emit(id);
    }
  }

  public emitUserRecordSelectedEvent(row: TableData) {
    if ('email' in row && 'bypassCount' in row) {
      this.userRecordSelected.emit(row['email'] as string);
    }
  }

  public emitSwapSupervisorEvent(id: string | undefined) {
    if (id) {
      this.swapSupervisorEvent.emit(id);
    }
  }

  protected readonly Number = Number;
  protected readonly String = String;


  public confirmReplaceLeadSupervisor($event: ISupervisor) {
    this.replaceLeadSupervisorEvent.emit({supervisor: $event, orgRow: this.modalRow});
    this.modalRow = undefined;
  }

  public emitSelectLeadSupervisorEvent(row: any) {
    if (row) {
      this.selectLeadSupervisorEvent.emit(row);
    }
  }

  public emitRetrySubmissionEvent(id: string | undefined): void {
    if (id) {
      this.retrySubmissionEvent.emit(id);
    }
  }

  public emitViewSubmissionEvent(id: string | undefined): void {
    if (id) {
      this.viewSubmissionErrorEvent.emit(id);
    }
  }

  public emitManualResolutionEvent(id: string | undefined) {
    if (id) {
      this.manualResolutionEvent.emit(id);
    }
  }

  public emitExportDataEvent() {
    this.exportDataEvent.emit();
  }

  public showRequestCell() {
    return this.hasViewRequestColumn || this.hasViewDetailsColumn || this.hasReviewRequestColumn || this.isAnnouncement || this.isSubmittedEvaluations ||
      this.isActiveVolunteers || this.hasViewColumn || this.hasEditColumn || this.hasReplaceLeadSupervisorColumn || this.isFailedSubmission || this.hasEditSupervisorColumn;
  }

  public formatHeaderText(header: string): string {
    const lowercasedHeader: string = header.toLowerCase();

    return lowercasedHeader.includes('timelogged')
    || lowercasedHeader.includes('time logged') ? 'Time Logged' : header;
  }

  public getActionItemsForPastOpportunity(opportunityId?: string): Observable<number> {
    if (!opportunityId) return of(0);

    return this.store.select(selectActionItemsByOpportunityId(opportunityId)).pipe(
      map((actionItems: any[]): number => actionItems.length),
      catchError((): Observable<number> => of(0))
    );
  }

  public emitEditSupervisorEvent(id?: string): void {
    if (id) {
      this.editSupervisorEvent.emit(id);
    }
  }

  private get activeStatuses(): string[] {
    const desiredStatuses: string[] = [

      // ContractStatus
      ContractStatus.ACTIVE,

      // ServiceStatus
      ServiceStatus.REGISTERED,
      ServiceStatus.PASSED,

      // OrganizationStatus
      OrganizationStatus.ACTIVE,

      // OpportunityStatus
      OpportunityStatus.ACTIVE,

      // EventLogActionType
      EventLogActionType.ACTIVATED,
      EventLogActionType.APPROVED,
    ];

    const removeDuplicates = new Set<string>(desiredStatuses) as Set<string>;
    return Array.from(removeDuplicates) as Array<string>;
  }

  private get pendingStatuses(): string[] {
    const desiredStatuses: string[] = [

      // ContractStatus
      ContractStatus.NEW,

      // ServiceStatus
      ServiceStatus.PENDING,
      ServiceStatus.PAST_REGISTRATION_PENDING_ADMIN_REVIEW,

      // OrganizationStatus
      OrganizationStatus.NEW,
      OrganizationStatus.PENDING_ADMIN_SIGNATURE,

      // EventLogActionType
      EventLogActionType.PENDING,
      EventLogActionType.CHANGES_REQUESTED,

    ];

    const removeDuplicates = new Set(desiredStatuses) as Set<string>;
    return Array.from(removeDuplicates) as Array<string>;
  }

  private get inactiveStatuses(): string[] {
    const desiredStatuses: string[] = [

      // ContractStatus
      ContractStatus.BLACKLISTED,
      ContractStatus.INACTIVE,

      // ServiceStatus
      ServiceStatus.DROPPED,
      ServiceStatus.FAILED,
      ServiceStatus.DENIED,

      // OrganizationStatus
      OrganizationStatus.INACTIVE,
      OrganizationStatus.DENIED,
      OrganizationStatus.BLACKLISTED,
      OrganizationStatus.REJECTED,

      // OpportunityStatus
      OpportunityStatus.INACTIVE,

      // EventLogActionType
      EventLogActionType.DEACTIVATED,
      EventLogActionType.DENIED,
      EventLogActionType.DROPPED,
      EventLogActionType.CHANGES_DENIED,

    ];

    const removeDuplicates = new Set(desiredStatuses) as Set<string>;
    return Array.from(removeDuplicates) as Array<string>;
  }

  private get newIconActionTypes(): string[] {
    return [EventLogActionType.CREATED];
  }

  private get approvedIconActionTypes(): string[] {
    return [
      EventLogActionType.ACTIVATED,
      EventLogActionType.APPROVED,
      EventLogActionType.BANNER_REGISTERED,
      EventLogActionType.CHANGES_APPROVED,
      EventLogActionType.COMMENT,
      EventLogActionType.EVAL_COMPLETED,
      EventLogActionType.GRADED,
      EventLogActionType.SUPERVISOR_CHANGED,
      EventLogActionType.SUPERVISOR_EVAL_COMPLETED,
    ];
  }

  private get rejectedIconActionTypes(): string[] {
    return [
      EventLogActionType.CANCELED,
      EventLogActionType.CHANGES_DENIED,
      EventLogActionType.DEACTIVATED,
      EventLogActionType.DENIED,
      EventLogActionType.DROPPED,
    ];
  }

  private get pendingIconActionTypes(): string[] {
    return [
      EventLogActionType.CHANGES_REQUESTED,
      EventLogActionType.DROP_REQUESTED,
      EventLogActionType.PENDING,
    ];
  }

  private checkSelectAllCheckboxIfApplicable() {
    const selectAllCheckbox: HTMLInputElement = this.checkboxForSelectAllElement;

    if (!selectAllCheckbox) return;

    this.checkIfAllSelectedFromRegistrationRequests
      ? selectAllCheckbox.checked = true
      : selectAllCheckbox.checked = false;
  }

  private get checkboxForSelectAllElement(): HTMLInputElement {
    return this.elementRef.nativeElement.querySelector('#selectAllCheckbox');
  }

  protected readonly ContractStatus = ContractStatus;
}
